import React from 'react'
import {Box, Button, Flex, Stack, Text} from '../../vanilla'
import {CheckCircleIcon} from '../../components/icons'
import {Link} from 'react-router-dom'
import {MealDealContinueBox} from './meal-deal-continue-box'
import {MealDealBuilderState, getMealDealSignature} from './meal-deal-builder-helper'
import {useBasketStore, useProductStore} from '../../store/hooks/useStore'
import {DisplayBasketItem, MealDealGroup} from '../../types/store/basket'
import {formatProductName} from '../../utils/utils'
import {ProductImage} from '../../components/product-detail/product-image'
import {cartItemImage} from '../cart/partials/styles.css'
import MealDealQuantityInput from './meal-deal-quantity-input'
import {Price} from '../../components/price'
import {usePromotion} from '../../store/hooks/usePromotion'
import {CalculatedPromoPills} from '../../components/promo-pill'
import useNavigation from '../../hooks/use-navigation'

export const MealDealCompletePrompt: React.FC<{
  resetBuilder: () => void
  builderState: MealDealBuilderState | null
  dealCost: number
  actualCost: number
}> = ({resetBuilder, builderState, dealCost, actualCost}) => {
  const {basket} = useBasketStore()
  const {productsById} = useProductStore()
  const promotion = usePromotion(builderState?.id)
  const navigate = useNavigation()

  if (!builderState) return <></>

  const builderDealSignature = getMealDealSignature(builderState.id, builderState.selectedProducts)
  const mealDealGroups = (basket?.mealdeals?.groupedItems || []) as MealDealGroup[]
  let matchedBasketGroup: MealDealGroup | null = null
  let dealitems: DisplayBasketItem[] = []
  const promoPrice = parseInt(promotion?.price || '')
  const promoDisplayPrice = `£${parseInt(promotion?.price || '0').toFixed(2)}`
  let quantity = 0

  mealDealGroups.forEach(function (group: MealDealGroup) {
    const groupID = group.promoID
    if (groupID !== builderState.id) return
    const groupItems = group?.items.map((x) => {
      return {productId: x.productID, quantity: x.displayQuantity}
    })
    const dealSignature = getMealDealSignature(builderState.id, groupItems)
    if (dealSignature === builderDealSignature) {
      matchedBasketGroup = group
      dealitems = group?.items || []
      quantity = group?.quantity || 0
    }
  })

  if (!matchedBasketGroup) {
    navigate('/basket', 'push')
    return <></>
  }

  return (
    <>
      <Flex flexWrap={'wrap'} width={'full'}>
        {matchedBasketGroup && (
          <Box
            alignItems="center"
            justifyContent="center"
            display="flex"
            flexDirection="column"
            gap="16px"
            width="full"
            alignSelf="center"
            background="lightGreen"
            border="1px"
            borderColor="green"
            borderRadius="slight"
            marginBottom={'18px'}
          >
            <Flex padding="16px" paddingBottom="0px" gap="8px" minWidth="full" flexDirection="row">
              <Stack>
                <CheckCircleIcon color="green" boxSize="32px" />
              </Stack>
              <Stack width={'full'}>
                <Text variant="heading1" data-test-selector="basket-deal-text">
                  Deal in Basket
                </Text>
                <Text variant="text2" data-test-selector="basket-confirmation-message">
                  Your deal has been added to your basket
                </Text>
              </Stack>
            </Flex>

            <Box width={'full'} paddingX="16px">
              <CalculatedPromoPills dealCost={dealCost} actualCost={actualCost} mealDealQuantity={quantity} />
            </Box>

            <Stack backgroundColor={'white'} padding="16px">
              <Flex flexWrap='wrap' padding="8px">
                {dealitems?.map((item: DisplayBasketItem) => {
                  const product = productsById[item.productID]
                  const productLink = `/p/${formatProductName(product?.name)}/${product?.id}.html`
                  const img =
                    product?.image_groups?.[0]?.images?.[0] ||
                    product?.imageGroups?.[0]?.images?.[0]
                  return (
                    <Flex flexBasis={['full', 'third']} alignItems="center" gap="12px" key={item.productID}>
                      <Flex
                        as={Link}
                        href={productLink}
                        ratio="1"
                        width="full"
                        position="relative"
                        className={cartItemImage}
                        marginBottom={'16px'}
                        paddingRight={'0px'}
                        to={productLink}
                      >
                        <ProductImage
                          image={img}
                          imageAlt={img?.alt}
                          isThumbnail={true}
                          inDrawer={false}
                          minHeight={['auto', 'full']}
                          style={{objectFit: 'contain'}}
                        />
                      </Flex>
                      <Text>{item.displayQuantity}x</Text>

                      <Text>{product?.name}</Text>
                    </Flex>
                  )
                })}
              </Flex>

              <Flex width={'full'} alignItems={'center'} justifyContent={'flex-end'}>
                <Text
                  variant="text3"
                  color="accent5"
                  textDecoration="underline"
                  data-test-selector="view-basket-button"
                  marginX={['0px', '12px']}
                  fontWeight={'semibold'}
                >
                  <Link to="/basket">View Basket</Link>
                </Text>

                <Flex
                  flexWrap={'wrap'}
                  justifyContent={'flex-end'}
                  textAlign={'right'}
                  marginX={'8px'}
                  lineHeight={'shorter'}
                >
                  <Price
                    promotionPrice={promoPrice}
                    price={promoPrice}
                    quantity={quantity}
                    priceInfo={{perUnit: '', wasPrice: ''}}
                    isBasket={true}
                  />
                  <Text
                    display={'block'}
                    textAlign={'right'}
                    variant="text0"
                    width={'full'}
                    lineHeight="shorter"
                  >
                    {promoDisplayPrice} per deal
                  </Text>
                </Flex>

                <MealDealQuantityInput
                  variant="outlineDark"
                  group={matchedBasketGroup}
                  inCart={false}
                  size="sm"
                  cartLocation="Meal Deal Complete"
                />
              </Flex>
            </Stack>
          </Box>
        )}

        <Box width={'full'} textAlign={'center'}>
          <Button
            variant="dark"
            onClick={() => resetBuilder()}
            size="lg"
            width={['full', 'auto']}
            data-test-selector="create-another-deal-button"
            marginBottom={'28px'}
          >
            Create Another Deal
          </Button>
          <MealDealContinueBox />
        </Box>
      </Flex>
    </>
  )
}
